import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../../config';
import { Award, Plus, Star, CheckCircle, X, Loader2 } from 'lucide-react';
import { useTranslation } from '../../i18n/LanguageContext';
import './Performance.css';

function SkillsManagement({ user, onNavigate, hideHeader = false, employeeId = null }) {
  const { t } = useTranslation();
  const [companySkills, setCompanySkills] = useState([]);
  const [employeeSkills, setEmployeeSkills] = useState([]);
  const [showNewSkillModal, setShowNewSkillModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createSkillLoading, setCreateSkillLoading] = useState(false);
  const [assignSkillLoading, setAssignSkillLoading] = useState(false);
  const [filters, setFilters] = useState({ category: '', is_active: 'true' });
  
  const [newSkill, setNewSkill] = useState({
    skill_name: '',
    skill_category: 'technical',
    description: '',
    levels: [
      { level: 1, name: 'Débutant' },
      { level: 2, name: 'Intermédiaire' },
      { level: 3, name: 'Avancé' },
      { level: 4, name: 'Expert' }
    ]
  });

  const [assignSkill, setAssignSkill] = useState({
    skill_id: '',
    employee_id: employeeId || '',
    level: 1,
    level_name: '',
    evidence: '',
    certificates: []
  });

  useEffect(() => {
    if (['admin', 'manager'].includes(user?.role)) {
      // Admin/Manager: can see/manage company skills and assign
      loadCompanySkills();
      loadEmployees();
      if (employeeId) {
        loadEmployeeSkills(employeeId);
      }
    } else if (user?.role === 'employee') {
      // Employee: only see assigned skills
      loadMySkills();
    }
  }, [filters, employeeId, user?.role]);

  const loadEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/employees?all=true&active=all&allRoles=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // ✅ CORRECTION : Gérer la structure paginée (data.employees ou data.data)
        if (data.success) {
          const employeesList = data.employees || data.data || [];
          if (Array.isArray(employeesList)) {
            // Mapper les employés pour s'assurer d'avoir first_name et last_name
            const mappedEmployees = employeesList.map(emp => ({
            id: emp.id,
            first_name: emp.firstName || emp.first_name || '',
            last_name: emp.lastName || emp.last_name || '',
            fullName: emp.fullName || `${emp.firstName || emp.first_name || ''} ${emp.lastName || emp.last_name || ''}`.trim()
            }));
            setEmployees(mappedEmployees);
          } else {
            setEmployees([]);
          }
        } else {
          setEmployees([]);
        }
      }
    } catch (error) {
      console.error('Erreur chargement employés:', error);
      setEmployees([]);
    }
  };

  const loadCompanySkills = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.is_active) params.append('is_active', filters.is_active);
      
      const res = await fetch(`${API_BASE}/api/performance/skills?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error(t('performance.error_loading'));
      
      const data = await res.json();
      setCompanySkills(data.skills || []);
    } catch (error) {
      console.error('Erreur chargement compétences:', error);
      alert(t('performance.error_loading'));
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeSkills = async (empId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/performance/employee-skills/${empId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Erreur lors du chargement');
      
      const data = await res.json();
      setEmployeeSkills(data.skills || []);
    } catch (error) {
      console.error('Erreur chargement compétences employé:', error);
    }
  };

  const loadMySkills = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/performance/my-skills`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erreur lors du chargement');
      const data = await res.json();
      setEmployeeSkills(data.skills || []);
    } catch (error) {
      console.error('Erreur chargement mes compétences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSkill = async () => {
    try {
      if (!newSkill.skill_name) {
        alert('Nom de compétence requis');
        return;
      }
      setCreateSkillLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/performance/skills`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSkill)
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Erreur lors de la création');
      }

      setShowNewSkillModal(false);
      setNewSkill({
        skill_name: '',
        skill_category: 'technical',
        description: '',
        levels: [
          { level: 1, name: 'Débutant' },
          { level: 2, name: 'Intermédiaire' },
          { level: 3, name: 'Avancé' },
          { level: 4, name: 'Expert' }
        ]
      });
      loadCompanySkills();
      alert('Compétence créée avec succès !');
    } catch (error) {
      console.error('Erreur création compétence:', error);
      alert(error.message || 'Erreur lors de la création');
    } finally {
      setCreateSkillLoading(false);
    }
  };

  const handleAssignSkill = async () => {
    try {
      if (!assignSkill.skill_id || !assignSkill.employee_id || !assignSkill.level) {
        alert('Veuillez remplir tous les champs');
        return;
      }
      setAssignSkillLoading(true);
      const selectedSkillData = companySkills.find(s => s.id === assignSkill.skill_id);
      const levelName = selectedSkillData?.levels?.find(l => l.level === assignSkill.level)?.name || `Niveau ${assignSkill.level}`;

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/performance/employee-skills`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...assignSkill,
          level_name: levelName
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Erreur lors de l\'attribution');
      }

      setShowAssignModal(false);
      setAssignSkill({
        skill_id: '',
        employee_id: employeeId || '',
        level: 1,
        level_name: '',
        evidence: '',
        certificates: []
      });
      if (employeeId) {
        loadEmployeeSkills(employeeId);
      }
      alert(t('performance.skill_assigned_success'));
    } catch (error) {
      console.error('Erreur attribution compétence:', error);
      alert(error.message || t('performance.error_assigning'));
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      technical: t('performance.category_technical'),
      soft_skills: t('performance.category_soft_skills'),
      language: t('performance.category_language'),
      certification: t('performance.category_certification'),
      other: t('performance.category_other')
    };
    return labels[category] || category;
  };

  const getStars = (level, maxLevel = 4) => {
    return Array.from({ length: maxLevel }, (_, i) => (
      <Star
        key={i}
        size={16}
        fill={i < level ? '#FBBF24' : 'transparent'}
        color="#FBBF24"
      />
    ));
  };

  if (loading) {
    return (
      <div className={hideHeader ? '' : 'performance-container'}>
        <div className="loading">{t('performance.loading')}</div>
      </div>
    );
  }

  return (
    <div className={hideHeader ? '' : 'performance-container'}>
      {!hideHeader && (
      <div className="objectives-header">
        <div>
          <h1><Award size={32} /> {t('performance.skills_title')}</h1>
          <p>{t('performance.skills_subtitle')}</p>
        </div>
        {['admin', 'manager'].includes(user?.role) && (
          <div style={{ display: 'flex', gap: '12px' }}>
            <button className="btn-primary" onClick={() => setShowNewSkillModal(true)}>
              <Plus size={20} /> {t('performance.new_skill')}
            </button>
            {!employeeId && (
              <button className="btn-primary" onClick={() => setShowAssignModal(true)}>
                <Plus size={20} /> {t('performance.assign_skill')}
              </button>
            )}
          </div>
        )}
      </div>
      )}
      
      {/* Boutons Nouveau (si header masqué) */}
      {hideHeader && user && (user.role === 'admin' || user.role === 'manager') && (
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button className="btn-primary" onClick={() => setShowNewSkillModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Plus size={20} /> {t('performance.new_skill')}
          </button>
          {!employeeId && (
            <button className="btn-primary" onClick={() => setShowAssignModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Plus size={20} /> {t('performance.assign_skill')}
            </button>
          )}
        </div>
      )}
      
      {/* Filtres (admin/manager only) */}
      {['admin', 'manager'].includes(user?.role) && (
      <div className="filters-section">
        <select
          value={filters.category}
          onChange={(e) => setFilters({ ...filters, category: e.target.value })}
        >
          <option value="">{t('performance.all_categories')}</option>
          <option value="technical">{t('performance.category_technical')}</option>
          <option value="soft_skills">{t('performance.category_soft_skills')}</option>
          <option value="language">{t('performance.category_language')}</option>
          <option value="certification">{t('performance.category_certification')}</option>
          <option value="other">{t('performance.category_other')}</option>
        </select>
      </div>
      )}

      {/* Compétences de l'entreprise (admin/manager only) */}
      {['admin', 'manager'].includes(user?.role) && !employeeId && (
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '20px', marginBottom: '16px' }}>{t('performance.company_skills')}</h2>
          <div className="objectives-grid">
            {companySkills.length === 0 ? (
              <div className="empty-state">
                <Award size={64} color="#9CA3AF" />
                <p>{t('performance.no_skills_defined')}</p>
              </div>
            ) : (
              companySkills.map((skill) => (
                <div key={skill.id} className="objective-card">
                  <div className="objective-header">
                    <div>
                      <h3>{skill.skill_name}</h3>
                      <p className="objective-meta">{getCategoryLabel(skill.skill_category)}</p>
                    </div>
                    {skill.is_active ? (
                      <CheckCircle size={16} color="#10B981" />
                    ) : (
                      <X size={16} color="#EF4444" />
                    )}
                  </div>
                  {skill.description && (
                    <p className="objective-description">{skill.description}</p>
                  )}
                  {skill.levels && (
                    <div style={{ marginTop: '12px' }}>
                      <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>{t('performance.levels_label')}</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {skill.levels.map((level) => (
                          <div key={level.level} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                            {getStars(level.level, skill.levels.length)}
                            <span>{level.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Mes compétences assignées (employee only) */}
      {user?.role === 'employee' && (
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '20px', marginBottom: '16px' }}>{t('performance.my_assigned_skills')}</h2>
          <div className="objectives-grid">
            {employeeSkills.length === 0 ? (
              <div className="empty-state">
                <Award size={64} color="#9CA3AF" />
                <p>{t('performance.no_employee_skills')}</p>
              </div>
            ) : (
              employeeSkills.map((es) => {
                const maxLevel = (es.levels && es.levels.length) || 4;
                return (
                  <div key={es.id} className="objective-card">
                    <div className="objective-header">
                      <div>
                        <h3>{es.skill_name}</h3>
                        <p className="objective-meta">{getCategoryLabel(es.skill_category)}</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {getStars(es.level, maxLevel || 4)}
                        <span style={{ fontSize: '14px', fontWeight: 600 }}>{es.level_name}</span>
                      </div>
                    </div>
                    {es.evidence && (
                      <p className="objective-description">
                        <strong>{t('performance.evidence_label')}</strong> {es.evidence}
                      </p>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Compétences de l'employé (admin/manager viewing specific employee) */}
      {employeeId && (
        <div>
          <h2 style={{ fontSize: '20px', marginBottom: '16px' }}>{t('performance.employee_skills')}</h2>
          <div className="objectives-grid">
            {employeeSkills.length === 0 ? (
              <div className="empty-state">
                <Award size={64} color="#9CA3AF" />
                <p>{t('performance.no_employee_skills')}</p>
              </div>
            ) : (
              employeeSkills.map((es) => {
                const skill = companySkills.find(s => s.id === es.skill_id);
                const maxLevel = skill?.levels?.length || 4;
                return (
                  <div key={es.id} className="objective-card">
                    <div className="objective-header">
                      <div>
                        <h3>{es.skill_name}</h3>
                        <p className="objective-meta">{getCategoryLabel(es.skill_category)}</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {getStars(es.level, maxLevel)}
                        <span style={{ fontSize: '14px', fontWeight: 600 }}>{es.level_name}</span>
                      </div>
                    </div>
                    {es.evidence && (
                      <p className="objective-description">
                        <strong>{t('performance.evidence_label')}</strong> {es.evidence}
                      </p>
                    )}
                    {es.last_verified_date && (
                      <div className="objective-info">
                        <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
                          {t('performance.verified_on')} {new Date(es.last_verified_date).toLocaleDateString(navigator.language || 'fr-FR')}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Modal Nouvelle Compétence */}
      {showNewSkillModal && (
        <div className="modal-overlay" onClick={() => setShowNewSkillModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('performance.create_skill')}</h2>
            
            <div className="form-group">
              <label>{t('performance.skill_name_label')}</label>
              <input
                type="text"
                value={newSkill.skill_name}
                onChange={(e) => setNewSkill({ ...newSkill, skill_name: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('performance.criterion_category_label')} *</label>
              <select
                value={newSkill.skill_category}
                onChange={(e) => setNewSkill({ ...newSkill, skill_category: e.target.value })}
              >
                <option value="technical">{t('performance.category_technical')}</option>
                <option value="soft_skills">{t('performance.category_soft_skills')}</option>
                <option value="language">{t('performance.category_language')}</option>
                <option value="certification">{t('performance.category_certification')}</option>
                <option value="other">{t('performance.category_other')}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t('performance.description_label')}</label>
              <textarea
                value={newSkill.description}
                onChange={(e) => setNewSkill({ ...newSkill, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>{t('performance.levels_label')}</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {newSkill.levels.map((level, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="number"
                      value={level.level}
                      onChange={(e) => {
                        const newLevels = [...newSkill.levels];
                        newLevels[index].level = parseInt(e.target.value) || 1;
                        setNewSkill({ ...newSkill, levels: newLevels });
                      }}
                      style={{ width: '60px', padding: '8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#fff' }}
                    />
                    <input
                      type="text"
                      value={level.name}
                      onChange={(e) => {
                        const newLevels = [...newSkill.levels];
                        newLevels[index].name = e.target.value;
                        setNewSkill({ ...newSkill, levels: newLevels });
                      }}
                      style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#fff' }}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowNewSkillModal(false)}>
                {t('performance.cancel')}
              </button>
              <button className="btn-primary" onClick={handleCreateSkill} disabled={createSkillLoading}>
                {createSkillLoading ? <><Loader2 size={18} className="spin" /> {t('performance.creating') || 'Creating…'}</> : t('performance.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Attribuer Compétence */}
      {showAssignModal && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('performance.assign_skill_title')}</h2>
            
            <div className="form-group">
              <label>{t('performance.employee_label')}</label>
              <select
                value={assignSkill.employee_id}
                onChange={(e) => setAssignSkill({ ...assignSkill, employee_id: e.target.value })}
                required
              >
                <option value="">{t('performance.select_employee')}</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{t('performance.skill_label')}</label>
              <select
                value={assignSkill.skill_id}
                onChange={(e) => {
                  const skill = companySkills.find(s => s.id === e.target.value);
                  setAssignSkill({
                    ...assignSkill,
                    skill_id: e.target.value,
                    level: 1,
                    level_name: skill?.levels?.[0]?.name || ''
                  });
                  setSelectedSkill(skill);
                }}
                required
              >
                <option value="">{t('performance.select_skill')}</option>
                {companySkills.filter(s => s.is_active).map((skill) => (
                  <option key={skill.id} value={skill.id}>
                    {skill.skill_name} ({getCategoryLabel(skill.skill_category)})
                  </option>
                ))}
              </select>
            </div>

            {selectedSkill && selectedSkill.levels && (
              <div className="form-group">
                <label>{t('performance.level_label')}</label>
                <select
                  value={assignSkill.level}
                  onChange={(e) => {
                    const level = parseInt(e.target.value);
                    const levelData = selectedSkill.levels.find(l => l.level === level);
                    setAssignSkill({
                      ...assignSkill,
                      level,
                      level_name: levelData?.name || ''
                    });
                  }}
                  required
                >
                  {selectedSkill.levels.map((level) => (
                    <option key={level.level} value={level.level}>
                      {t('performance.level_format', { number: level.level, name: level.name })}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>{t('performance.evidence_justification_label')}</label>
              <textarea
                value={assignSkill.evidence}
                onChange={(e) => setAssignSkill({ ...assignSkill, evidence: e.target.value })}
                rows={3}
                placeholder={t('performance.evidence_placeholder')}
              />
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowAssignModal(false)}>
                {t('performance.cancel')}
              </button>
              <button className="btn-primary" onClick={handleAssignSkill} disabled={assignSkillLoading}>
                {assignSkillLoading ? <><Loader2 size={18} className="spin" /> {t('performance.assigning') || 'Assigning…'}</> : t('performance.assign_button')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SkillsManagement;

